import { usePrivy } from "@privy-io/react-auth"
import {
	useCreateWallet as useCreateSolanaWallet,
	useWallets as useSolanaWallets,
} from "@privy-io/react-auth/solana"
import { useAction, useConvexAuth, useQuery } from "convex/react"
import { usePostHog } from "posthog-js/react"
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { getErrorMessage } from "@/lib/errors"
import {
	captureSentryException,
	captureSentryMessage,
	clearSentryUser,
	setSentryUser,
} from "@/lib/sentry"
import { api } from "~convex/_generated/api"

const CONVEX_SYNC_GRACE_MS = 5_000
const USER_RESOLUTION_GRACE_MS = 5_000
const MAX_ENSURE_ATTEMPTS = 3
const ENSURE_RETRY_DELAY_MS = 1_500

const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms))

function isWalletAlreadyCreatedError(message: string) {
	const lower = message.toLowerCase()
	return lower.includes("already exists") || lower.includes("already has")
}

function getSetupErrorMessage(error: unknown) {
	const message = getErrorMessage(error)
	if (message === "Wallet setup timed out") {
		return "We couldn't finish creating your wallet. Retry to continue."
	}
	return message
}

function shouldResetSessionAfterBootstrapError(message: string) {
	return (
		message === "Wallet setup timed out" ||
		message === "Not authenticated" ||
		message === "Could not verify your account. Please sign out and try again."
	)
}

function describeUserState(user: ReturnType<typeof useQuery>) {
	if (user === undefined) {
		return "query_loading"
	}
	if (user === null) {
		return "missing"
	}
	return "present"
}

type SetupState =
	| { status: "idle"; message: null }
	| { status: "running"; message: null }
	| { status: "error"; message: string; requiresSessionReset: boolean }

function useAppAuthInternal() {
	const {
		authenticated: privyAuthenticated,
		logout,
		ready: privyReady,
		user: privyUser,
	} = usePrivy()
	const { createWallet } = useCreateSolanaWallet()
	const { ready: walletsReady, wallets } = useSolanaWallets()
	const { isAuthenticated: convexAuthenticated } = useConvexAuth()
	const user = useQuery(api.users.getCurrentUser, convexAuthenticated ? {} : "skip")
	const ensureOnLogin = useAction(api.auth.ensureOnLogin.ensureOnLogin)
	const posthog = usePostHog()
	const ensureOnLoginRef = useRef(ensureOnLogin)
	ensureOnLoginRef.current = ensureOnLogin
	const createWalletRef = useRef(createWallet)
	createWalletRef.current = createWallet
	const setupInFlightRef = useRef(false)
	const bootstrapAttemptRef = useRef(0)
	const retryCountRef = useRef(0)
	const [hasKnownSession, setHasKnownSession] = useState(false)
	const [bootstrapAttempted, setBootstrapAttempted] = useState(false)
	const [convexSyncExpired, setConvexSyncExpired] = useState(false)
	const [userResolutionTimedOut, setUserResolutionTimedOut] = useState(false)
	const [setupState, setSetupState] = useState<SetupState>({
		status: "idle",
		message: null,
	})

	const hasSession = privyReady && privyAuthenticated && !!privyUser
	const hasSolanaWallet = wallets.length > 0
	const buildAuthDebugContext = useCallback(
		() => ({
			bootstrapAttempted,
			convexAuthenticated,
			hasKnownSession,
			hasSession,
			hasSolanaWallet,
			location: `${window.location.pathname}${window.location.search}`,
			privyAuthenticated,
			privyReady,
			privyUserId: privyUser?.id ?? null,
			setupStatus: setupState.status,
			userState: describeUserState(user),
			walletCount: wallets.length,
			walletsReady,
		}),
		[
			bootstrapAttempted,
			convexAuthenticated,
			hasKnownSession,
			hasSession,
			hasSolanaWallet,
			privyAuthenticated,
			privyReady,
			privyUser?.id,
			setupState.status,
			user,
			wallets.length,
			walletsReady,
		],
	)

	const runBootstrap = useCallback(async () => {
		if (!hasSession || !convexAuthenticated || user !== null || setupInFlightRef.current) {
			return
		}

		bootstrapAttemptRef.current += 1
		setupInFlightRef.current = true
		setBootstrapAttempted(true)
		setSetupState({ status: "running", message: null })

		try {
			let requestedWalletCreation = false

			for (let attempt = 1; attempt <= MAX_ENSURE_ATTEMPTS; attempt++) {
				const result = await ensureOnLoginRef.current({})
				if (result.status !== "pending_wallet") {
					setSetupState({ status: "idle", message: null })
					return
				}

				if (walletsReady && !hasSolanaWallet && !requestedWalletCreation) {
					await createWalletRef.current().catch((error: unknown) => {
						const message = getErrorMessage(error)
						if (!isWalletAlreadyCreatedError(message)) {
							throw error
						}
					})
					requestedWalletCreation = true
				}

				if (attempt < MAX_ENSURE_ATTEMPTS) {
					await sleep(ENSURE_RETRY_DELAY_MS)
				}
			}

			throw new Error("Wallet setup timed out")
		} catch (error: unknown) {
			const originalMessage = getErrorMessage(error)
			captureSentryException(error, {
				extra: {
					...buildAuthDebugContext(),
					bootstrapAttempt: bootstrapAttemptRef.current,
					errorMessage: originalMessage,
				},
				tags: {
					stage: "app_auth_bootstrap",
				},
			})
			setSetupState({
				status: "error",
				message: getSetupErrorMessage(error),
				requiresSessionReset: shouldResetSessionAfterBootstrapError(originalMessage),
			})
		} finally {
			setupInFlightRef.current = false
		}
	}, [buildAuthDebugContext, convexAuthenticated, hasSession, hasSolanaWallet, user, walletsReady])

	useEffect(() => {
		if (
			convexAuthenticated &&
			user === null &&
			setupState.status === "idle" &&
			!bootstrapAttempted
		) {
			runBootstrap().catch(() => {})
		}
	}, [bootstrapAttempted, convexAuthenticated, runBootstrap, setupState.status, user])

	useEffect(() => {
		if (hasSession) {
			setHasKnownSession(true)
			return
		}

		if (privyReady && !privyAuthenticated && !privyUser) {
			setHasKnownSession(false)
		}
	}, [hasSession, privyAuthenticated, privyReady, privyUser])

	useEffect(() => {
		if (!hasSession || user) {
			setBootstrapAttempted(false)
		}
	}, [hasSession, user])

	useEffect(() => {
		if (!hasKnownSession || convexAuthenticated) {
			setConvexSyncExpired(false)
			return
		}

		const timeoutId = window.setTimeout(() => {
			captureSentryMessage("auth_convex_sync_expired", {
				extra: buildAuthDebugContext(),
				level: "warning",
			})
			setConvexSyncExpired(true)
		}, CONVEX_SYNC_GRACE_MS)

		return () => {
			window.clearTimeout(timeoutId)
		}
	}, [buildAuthDebugContext, convexAuthenticated, hasKnownSession])

	useEffect(() => {
		const waitingForUserRecord =
			convexAuthenticated &&
			(user === undefined || (user === null && bootstrapAttempted && setupState.status === "idle"))

		if (!waitingForUserRecord) {
			setUserResolutionTimedOut(false)
			return
		}

		const timeoutId = window.setTimeout(() => {
			captureSentryMessage("auth_user_resolution_timed_out", {
				extra: buildAuthDebugContext(),
				level: "warning",
			})
			setUserResolutionTimedOut(true)
		}, USER_RESOLUTION_GRACE_MS)

		return () => {
			window.clearTimeout(timeoutId)
		}
	}, [bootstrapAttempted, buildAuthDebugContext, convexAuthenticated, setupState.status, user])

	useEffect(() => {
		const loggedOut = privyReady && !privyAuthenticated && !privyUser
		if (loggedOut || user) {
			setSetupState({ status: "idle", message: null })
		}
	}, [privyAuthenticated, privyReady, privyUser, user])

	useEffect(() => {
		if (user) {
			setSentryUser(user._id, user.handle)
		} else if (!convexAuthenticated) {
			clearSentryUser()
		}
	}, [convexAuthenticated, user])

	useEffect(() => {
		if (user) {
			posthog.identify(user._id, {
				display_name: user.display_name,
				handle: user.handle,
				wallet_address: user.wallet_address,
			})
			return
		}

		if (!convexAuthenticated) {
			posthog.reset()
		}
	}, [convexAuthenticated, posthog, user])

	const waitingForConvexSession = hasKnownSession && !convexAuthenticated && !convexSyncExpired
	const waitingToBootstrap =
		convexAuthenticated && user === null && setupState.status === "idle" && !bootstrapAttempted
	const waitingForUserQuery = convexAuthenticated && user === undefined
	const waitingForBootstrapResult =
		convexAuthenticated && user === null && setupState.status === "idle" && bootstrapAttempted
	const loading =
		(!privyReady && !hasKnownSession) ||
		waitingForConvexSession ||
		waitingToBootstrap ||
		(waitingForUserQuery && !userResolutionTimedOut) ||
		(waitingForBootstrapResult && !userResolutionTimedOut) ||
		setupState.status === "running"
	const authed = convexAuthenticated && user != null
	const requiresSetup = hasKnownSession && !loading && !authed
	const requiresSessionReset =
		requiresSetup &&
		(!convexAuthenticated ||
			userResolutionTimedOut ||
			(setupState.status === "error" && setupState.requiresSessionReset))
	const currentUser = user ?? null
	const setupMessage = requiresSetup
		? convexAuthenticated
			? requiresSessionReset
				? "We couldn't restore your account session. Sign out and try again."
				: (setupState.message ?? "We couldn't finish setting up your account. Retry to continue.")
			: "We couldn't restore your session. Retry to continue."
		: null

	const retrySetup = useCallback(() => {
		if (!hasKnownSession) {
			return
		}

		retryCountRef.current += 1
		posthog.capture("auth_retry_setup_clicked", {
			...buildAuthDebugContext(),
			requiresSessionReset,
			retryCount: retryCountRef.current,
		})

		if (requiresSessionReset) {
			Promise.race([logout().catch(() => {}), sleep(1_500)])
				.catch(() => {})
				.finally(() => {
					window.location.assign(window.location.pathname + window.location.search)
				})
			return
		}

		setBootstrapAttempted(false)
		setSetupState({ status: "idle", message: null })
		setUserResolutionTimedOut(false)
	}, [buildAuthDebugContext, hasKnownSession, logout, posthog, requiresSessionReset])

	return useMemo(
		() => ({
			currentUser,
			hasSession: hasKnownSession,
			isAuthenticated: authed,
			isLoading: loading,
			requiresSetup,
			requiresSessionReset,
			retrySetup,
			setupMessage,
		}),
		[
			currentUser,
			authed,
			hasKnownSession,
			loading,
			requiresSessionReset,
			requiresSetup,
			retrySetup,
			setupMessage,
		],
	)
}

type AppAuthState = ReturnType<typeof useAppAuthInternal>

const AppAuthContext = createContext<AppAuthState>({
	currentUser: null,
	hasSession: false,
	isAuthenticated: false,
	isLoading: true,
	requiresSetup: false,
	requiresSessionReset: false,
	retrySetup: () => {},
	setupMessage: null,
})

export function AppAuthProvider({ children }: { children: ReactNode }) {
	const state = useAppAuthInternal()
	return <AppAuthContext.Provider value={state}>{children}</AppAuthContext.Provider>
}

export function useAppAuth() {
	return useContext(AppAuthContext)
}
